import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  ChatBaseMessage,
  ChatMessage,
  RawChatMessage,
  TypingMessage,
} from '../models/chat-message';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { MessageType } from '../models/message-type';
import { AuthService } from '@fc-core/services';

// Connection state machine types
export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'offline';

export interface ConnectionStateInfo {
  state: ConnectionState;
  attempts: number;
  error: string | null;
}

// Error classification types
export interface ErrorClassification {
  shouldReconnect: boolean;
  userMessage: string;
  delay?: number;
}

// Message queue types
interface ViewedReceipt {
  type: MessageType.VIEWED;
  channel: ChatMessage['channel'];
  messageId: string;
  id: string;
}

type OutgoingChatPayload = ChatMessage | RawChatMessage | ViewedReceipt;

interface QueuedMessage {
  message: OutgoingChatPayload;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class ChatWsService implements OnDestroy {
  private ws: WebSocket | null = null;
  private socketGeneration = 0;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private sleepWatchdogTimer: ReturnType<typeof setInterval> | null = null;
  private lastWatchdogTick = Date.now();
  // Track when the tab was last hidden so visibilitychange can decide
  // whether to trust the existing socket or force a reconnect on resume.
  private hiddenSince: number | null = null;
  private readonly VISIBILITY_FORCE_RECONNECT_MS = 30000;
  private hasEverConnected = false;
  private clientClosed = false;
  private destroyed = false;
  private browserOffline =
    typeof navigator !== 'undefined' && navigator.onLine === false;

  // Activity monitoring for zombie connection detection
  private lastActivityTime = Date.now();
  private activityCheckInterval: ReturnType<typeof setInterval> | null = null;
  private readonly BASE_RECONNECT_DELAY_MS = 1000;
  private readonly MAX_RECONNECT_DELAY_MS = 30000;
  private readonly STALE_CLOSE_CODE = 4000;
  private readonly SEND_FAILURE_CLOSE_CODE = 4001;
  private readonly ACTIVITY_TIMEOUT = 120000; // 2 minutes without any message
  private readonly ACTIVITY_CHECK_INTERVAL = 30000; // Check every 30s

  // Message queue for messages sent during disconnection
  private messageQueue: QueuedMessage[] = [];
  private readonly MAX_QUEUE_SIZE = 50;
  private readonly MAX_QUEUE_AGE_MS = 300000; // 5 minutes

  // State machine - single source of truth
  connectionState = signal<ConnectionStateInfo>({
    state: 'disconnected',
    attempts: 0,
    error: null,
  });

  // Backwards-compatible derived signals
  wsConnected = computed(() => this.connectionState().state === 'connected');
  reconnecting = computed(
    () => this.connectionState().state === 'reconnecting',
  );
  isOffline = computed(() => this.connectionState().state === 'offline');

  // Expose error message for UI
  connectionError = computed(() => this.connectionState().error);
  reconnectAttemptCount = computed(() => this.connectionState().attempts);

  chatStream = new Subject<ChatBaseMessage>();
  chatStream$ = this.chatStream.asObservable();
  auth = inject(AuthService);

  constructor() {
    this.attachBrowserEventListeners();
    this.startSleepWatchdog();
    this.startActivityMonitor();
    if (this.browserOffline) {
      this.updateState('offline', 'You are offline');
    } else {
      this.connect();
    }
  }

  connect(): void {
    if (this.destroyed) return;
    if (this.isOfflineNow()) {
      this.handleOffline();
      return;
    }
    if (this.hasActiveSocket()) return;

    const token = localStorage.getItem('token');
    if (!token) {
      this.clientClosed = true;
      this.updateState('disconnected', 'Session expired');
      this.clearReconnectTimer();
      this.auth.logout();
      return;
    }

    this.clientClosed = false;
    if (this.hasEverConnected) {
      this.updateState('reconnecting');
    } else {
      this.updateState('connecting');
    }

    const generation = ++this.socketGeneration;
    const url = environment.chatWsUrl + token;
    let socket: WebSocket;

    try {
      socket = new WebSocket(url);
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      this.scheduleReconnect('Connection error');
      return;
    }

    this.ws = socket;

    socket.onopen = () => {
      if (!this.isCurrentSocket(socket, generation)) return;
      this.updateState('connected');
      this.hasEverConnected = true;
      this.reconnectAttempts = 0;
      this.lastActivityTime = Date.now();
      this.clearReconnectTimer();
      this.flushMessageQueue();
    };

    socket.onmessage = (event) => {
      if (!this.isCurrentSocket(socket, generation)) return;
      this.lastActivityTime = Date.now();
      try {
        const data = JSON.parse(event.data);
        this.chatStream.next(data as ChatBaseMessage);
      } catch (error) {
        console.error('Invalid WebSocket message:', error, event.data);
      }
    };

    socket.onclose = (event) => {
      if (!this.isCurrentSocket(socket, generation)) return;
      console.log('WebSocket closed:', event);
      this.ws = null;

      const classification = this.classifyCloseCode(event.code);

      // On archived employee (1008), force logout and stop reconnect logic
      if (event.code === 1008) {
        this.clientClosed = true;
        this.updateState('disconnected', classification.userMessage);
        this.clearReconnectTimer();
        this.auth.logout();
        return;
      }

      if (this.destroyed || this.clientClosed) {
        this.updateState('disconnected', classification.userMessage);
        return;
      }

      if (this.isOfflineNow()) {
        this.handleOffline();
        return;
      }

      this.scheduleReconnect(classification.userMessage, {
        minimumDelayMs: classification.delay,
      });
    };

    socket.onerror = (event) => {
      if (!this.isCurrentSocket(socket, generation)) return;
      console.error('WebSocket error:', event);
      this.closeSocketForReconnect(socket, 'Connection error');
    };
  }

  private updateState(
    state: ConnectionState,
    error: string | null = null,
  ): void {
    const currentAttempts = this.connectionState().attempts;
    this.connectionState.set({
      state,
      attempts: state === 'connected' ? 0 : currentAttempts,
      error: state === 'connected' ? null : error,
    });
  }

  private classifyCloseCode(code: number): ErrorClassification {
    const classifications: Record<number, ErrorClassification> = {
      1000: { shouldReconnect: true, userMessage: 'Connection closed' },
      1001: { shouldReconnect: true, userMessage: 'Server going away' },
      1002: { shouldReconnect: true, userMessage: 'Protocol error' },
      1003: { shouldReconnect: true, userMessage: 'Unsupported data' },
      1006: { shouldReconnect: true, userMessage: 'Connection lost' },
      1007: { shouldReconnect: true, userMessage: 'Invalid data' },
      1008: { shouldReconnect: false, userMessage: 'Session expired' },
      1009: { shouldReconnect: true, userMessage: 'Message too large' },
      1011: { shouldReconnect: true, userMessage: 'Server error', delay: 5000 },
      1012: {
        shouldReconnect: true,
        userMessage: 'Server restarting',
        delay: 3000,
      },
      1013: {
        shouldReconnect: true,
        userMessage: 'Try again later',
        delay: 10000,
      },
      1014: { shouldReconnect: true, userMessage: 'Bad gateway' },
      1015: { shouldReconnect: true, userMessage: 'TLS handshake failed' },
      4000: { shouldReconnect: true, userMessage: 'Connection timeout' }, // Activity timeout
      4001: { shouldReconnect: true, userMessage: 'Message delivery failed' },
    };
    return (
      classifications[code] || {
        shouldReconnect: true,
        userMessage: 'Connection error',
      }
    );
  }

  private scheduleReconnect(
    reason: string,
    options: {
      delayMs?: number;
      minimumDelayMs?: number;
      countAttempt?: boolean;
    } = {},
  ): void {
    if (!this.canReconnect()) return;
    if (this.hasActiveSocket()) return;

    this.updateState('reconnecting', reason);
    this.clearReconnectTimer();

    const delay =
      options.delayMs ??
      Math.max(this.calculateReconnectDelay(), options.minimumDelayMs ?? 0);

    console.log(
      `Attempting to reconnect in ${Math.round(delay) / 1000} seconds...`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (!this.canReconnect() || this.hasActiveSocket()) return;
      if (options.countAttempt !== false) {
        this.reconnectAttempts++;
        this.connectionState.update((current) => ({
          ...current,
          attempts: this.reconnectAttempts,
        }));
      }
      this.connect();
    }, delay);
  }

  private calculateReconnectDelay(): number {
    const base =
      this.BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts);
    const jitter = base * (0.2 * Math.random());
    return Math.min(base + jitter, this.MAX_RECONNECT_DELAY_MS);
  }

  private canReconnect(): boolean {
    return !this.destroyed && !this.clientClosed && !this.isOfflineNow();
  }

  private hasActiveSocket(): boolean {
    return (
      this.ws?.readyState === WebSocket.CONNECTING ||
      this.ws?.readyState === WebSocket.OPEN
    );
  }

  private isCurrentSocket(socket: WebSocket, generation: number): boolean {
    return this.ws === socket && this.socketGeneration === generation;
  }

  private isOfflineNow(): boolean {
    return (
      this.browserOffline ||
      (typeof navigator !== 'undefined' && navigator.onLine === false)
    );
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  // Resume paths (OS wake, tab visible, online) can't trust the
  // existing socket's readyState — after a half-open TCP it still
  // reads OPEN even though no frames will ever arrive. Force-close
  // before scheduling reconnect so scheduleReconnect's hasActiveSocket
  // guard doesn't silently no-op the recovery.
  private forceReconnect(reason: string): void {
    if (!this.canReconnect()) return;
    this.closeCurrentSocket();
    this.scheduleReconnect(reason, { delayMs: 0, countAttempt: false });
  }

  private closeCurrentSocket(): void {
    const socket = this.ws;
    if (!socket) return;

    this.ws = null;
    this.socketGeneration++;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;

    try {
      socket.close();
    } catch (_) {
      // ignore
    }
  }

  private closeSocketForReconnect(socket: WebSocket, reason: string): void {
    if (this.ws !== socket) return;
    if (!this.canReconnect()) return;

    this.updateState('reconnecting', reason);
    try {
      socket.close();
    } catch (_) {
      this.ws = null;
      this.scheduleReconnect(reason);
    }
  }

  // Activity monitoring to detect zombie/stale connections
  private startActivityMonitor(): void {
    this.activityCheckInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const timeSinceActivity = Date.now() - this.lastActivityTime;
        if (timeSinceActivity > this.ACTIVITY_TIMEOUT) {
          console.warn(
            `Connection appears stale (no activity for ${Math.round(timeSinceActivity / 1000)}s) - forcing reconnect`,
          );
          try {
            this.ws?.close(this.STALE_CLOSE_CODE, 'Activity timeout');
          } catch (_) {
            this.ws = null;
            this.scheduleReconnect('Connection timeout');
          }
        }
      }
    }, this.ACTIVITY_CHECK_INTERVAL);
  }

  private stopActivityMonitor(): void {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
  }

  // Message queue methods
  private queueMessage(message: OutgoingChatPayload): void {
    // Remove expired messages first
    this.pruneMessageQueue();

    if (this.messageQueue.length >= this.MAX_QUEUE_SIZE) {
      // Evict a viewed receipt before an actual message — receipts are
      // recoverable (the message stays unread), typed messages are not.
      const oldestReceiptIndex = this.messageQueue.findIndex(
        (queued) => queued.message.type === MessageType.VIEWED,
      );
      if (oldestReceiptIndex !== -1) {
        this.messageQueue.splice(oldestReceiptIndex, 1);
      } else {
        this.messageQueue.shift();
      }
    }

    this.messageQueue.push({
      message,
      timestamp: Date.now(),
    });

    console.log(
      `Message queued for later delivery (queue size: ${this.messageQueue.length})`,
    );
  }

  private pruneMessageQueue(): void {
    const now = Date.now();
    this.messageQueue = this.messageQueue.filter(
      (qm) => now - qm.timestamp < this.MAX_QUEUE_AGE_MS,
    );
  }

  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;

    this.pruneMessageQueue();

    const queuedCount = this.messageQueue.length;
    if (queuedCount > 0) {
      console.log(`Flushing ${queuedCount} queued messages`);
    }

    const pending = this.messageQueue;
    this.messageQueue = [];

    for (let index = 0; index < pending.length; index++) {
      const queued = pending[index];
      if (!this.sendPayload(queued.message)) {
        this.messageQueue = pending.slice(index);
        this.scheduleReconnect('Message delivery failed', {
          delayMs: 0,
          countAttempt: false,
        });
        return;
      }
    }
  }

  sendMessage(message: ChatMessage | RawChatMessage): void {
    this.sendOrQueue(message);
  }

  sendTyping(message: TypingMessage): void {
    // Don't queue typing indicators - they are ephemeral
    this.sendPayload(message);
  }

  sendViewed(message: ChatMessage): void {
    // Viewed receipts drive the server-side unread counts — queue them
    // like regular messages so a receipt sent while disconnected isn't
    // silently dropped (the badge would stay wrong until a full reload).
    this.sendOrQueue({
      type: MessageType.VIEWED,
      channel: message.channel,
      messageId: message.id,
      id: uuidv4(),
    });
  }

  private sendOrQueue(payload: OutgoingChatPayload): void {
    if (this.sendPayload(payload)) return;

    // Queue payload for later delivery instead of losing it
    this.queueMessage(payload);
    if (this.canReconnect()) {
      this.scheduleReconnect('Connection lost', {
        delayMs: 0,
        countAttempt: false,
      });
    }
  }

  private sendPayload(payload: unknown): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;

    try {
      this.ws.send(JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error('WebSocket send failed:', error);
      try {
        this.ws.close(this.SEND_FAILURE_CLOSE_CODE, 'Send failed');
      } catch (_) {
        this.ws = null;
      }
      return false;
    }
  }

  close(): void {
    this.clientClosed = true;
    this.clearReconnectTimer();
    this.updateState('disconnected');
    this.closeCurrentSocket();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.detachBrowserEventListeners();
    this.stopActivityMonitor();
    if (this.sleepWatchdogTimer) {
      clearInterval(this.sleepWatchdogTimer);
      this.sleepWatchdogTimer = null;
    }
    this.close();
  }

  // Browser/network awareness
  private attachBrowserEventListeners(): void {
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);
  }

  private detachBrowserEventListeners(): void {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onWindowFocus);
  }

  private onOnline = () => {
    this.browserOffline = false;
    // Respect intentional closes (logout, session expiry) - don't resurrect
    // the connection with a stale token just because the network came back.
    if (this.clientClosed) return;
    this.reconnectAttempts = 0;
    this.connectionState.set({
      state: 'disconnected',
      attempts: 0,
      error: null,
    });
    // Browser regained network — any prior socket is a half-open zombie.
    this.forceReconnect('Back online');
  };

  private onOffline = () => {
    this.handleOffline();
  };

  private handleOffline(): void {
    this.browserOffline = true;
    this.updateState('offline', 'You are offline');
    this.clearReconnectTimer();
    this.closeCurrentSocket();
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.hiddenSince = Date.now();
      return;
    }
    // Tab visible again. If it was hidden long enough that the OS or
    // a network intermediary likely dropped the TCP socket, force a
    // reconnect rather than trusting readyState (which still reads
    // OPEN on half-open sockets).
    const hiddenFor =
      this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0;
    this.hiddenSince = null;
    if (hiddenFor >= this.VISIBILITY_FORCE_RECONNECT_MS) {
      this.forceReconnect('Tab resumed');
    } else {
      this.scheduleReconnect('Connection check', {
        delayMs: 0,
        countAttempt: false,
      });
    }
  };

  private onWindowFocus = () => {
    this.scheduleReconnect('Connection check', {
      delayMs: 0,
      countAttempt: false,
    });
  };

  private startSleepWatchdog(): void {
    const intervalMs = 15000;
    this.lastWatchdogTick = Date.now();
    this.sleepWatchdogTimer = setInterval(() => {
      const now = Date.now();
      const delta = now - this.lastWatchdogTick;
      this.lastWatchdogTick = now;
      // setInterval pauses during OS sleep; a delta much larger than
      // the configured interval means we just woke up and the socket
      // is almost certainly a half-open zombie.
      if (delta > intervalMs * 3) {
        this.forceReconnect('System resumed');
      }
    }, intervalMs);
  }

  tryReconnectNow(): void {
    if (this.isOfflineNow()) return;
    this.clientClosed = false;
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.updateState('reconnecting');
    // Manual user-driven reconnect — assume the existing socket is
    // suspect and tear it down before redialing.
    this.forceReconnect('Reconnecting');
  }

  // Utility method to get queue status (useful for debugging)
  getQueueStatus(): { size: number; oldestAge: number | null } {
    this.pruneMessageQueue();
    const oldestMessage = this.messageQueue[0];
    return {
      size: this.messageQueue.length,
      oldestAge: oldestMessage ? Date.now() - oldestMessage.timestamp : null,
    };
  }
}
